import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Check, HelpCircle, X } from 'lucide-react'
import type { LinkStatus } from '../../../../types/infrastructure-map'

export const TOTAL_STEPS = 3
export const ID_DISPLAY_LENGTH = 8
const STEPS = [1, 2, TOTAL_STEPS] as const

const LINK_STATUS_COLOURS: Record<LinkStatus, string> = {
  confirmed:
    'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700',
  suspected:
    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700',
  'open-question':
    'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700',
}

const LINK_STATUS_I18N_KEYS: Record<LinkStatus, { label: string; description: string }> = {
  confirmed: {
    label: 'infrastructure.canvas.linkStatusConfirmed',
    description: 'infrastructure.canvas.linkStatusConfirmedDesc',
  },
  suspected: {
    label: 'infrastructure.canvas.linkStatusSuspected',
    description: 'infrastructure.canvas.linkStatusSuspectedDesc',
  },
  'open-question': {
    label: 'infrastructure.canvas.linkStatusOpenQuestion',
    description: 'infrastructure.canvas.linkStatusOpenQuestionDesc',
  },
}

interface ToolHeaderProps {
  onCancel: () => void
  t: TFunction<'cases'>
}

export function ToolHeader({ onCancel, t }: ToolHeaderProps): ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        {t('infrastructure.canvas.createLink')}
      </h2>
      <button
        onClick={onCancel}
        className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface ProgressStepsProps {
  currentStep: number
  t: TFunction<'cases'>
}

export function ProgressSteps({ currentStep, t }: ProgressStepsProps): ReactElement {
  return (
    <div className="border-b border-slate-200 bg-slate-50 px-6 py-3 dark:border-slate-700 dark:bg-slate-900/50">
      <div className="flex items-center gap-2">
        {STEPS.map((step) => (
          <StepIndicator key={step} step={step} currentStep={currentStep} />
        ))}
      </div>
      <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {currentStep === 1 && t('infrastructure.canvas.step1SelectParent')}
        {currentStep === 2 && t('infrastructure.canvas.step2SelectChildren')}
        {currentStep === TOTAL_STEPS && t('infrastructure.canvas.step3ChooseType')}
      </div>
    </div>
  )
}

function stepCircleClass(step: number, currentStep: number): string {
  if (step < currentStep) return 'bg-fuchsia-500 text-white'
  if (step === currentStep) {
    return 'bg-fuchsia-100 dark:bg-fuchsia-900/30 text-fuchsia-600 dark:text-fuchsia-400 ring-2 ring-fuchsia-500'
  }
  return 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
}

function StepIndicator({ step, currentStep }: { step: number; currentStep: number }): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${stepCircleClass(step, currentStep)}`}
      >
        {step < currentStep ? <Check className="h-3 w-3" /> : step}
      </div>
      {step < TOTAL_STEPS && (
        <div
          className={`h-0.5 w-8 ${step < currentStep ? 'bg-fuchsia-500' : 'bg-slate-200 dark:bg-slate-700'}`}
        />
      )}
    </div>
  )
}

interface ParentSelectionProps {
  parentEndpointId: string | null | undefined
  t: TFunction<'cases'>
}

export function ParentSelection({ parentEndpointId, t }: ParentSelectionProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.canvas.parentEndpoint')}
      </label>
      <div
        className={`rounded-lg border px-3 py-2 ${
          parentEndpointId
            ? 'border-fuchsia-300 bg-fuchsia-50 dark:border-fuchsia-700 dark:bg-fuchsia-900/20'
            : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50'
        }`}
      >
        {parentEndpointId ? (
          <span className="font-mono text-sm text-fuchsia-700 dark:text-fuchsia-300">
            {parentEndpointId.slice(0, ID_DISPLAY_LENGTH)}...
          </span>
        ) : (
          <span className="text-sm text-slate-400 dark:text-slate-500">
            {t('infrastructure.canvas.clickEndpointOnCanvas')}
          </span>
        )}
      </div>
    </div>
  )
}

interface ChildrenSelectionProps {
  parentEndpointId: string | null | undefined
  childEndpointIds: string[]
  onToggleChild: (id: string) => void
  t: TFunction<'cases'>
}

export function ChildrenSelection(props: ChildrenSelectionProps): ReactElement {
  const { parentEndpointId, childEndpointIds, onToggleChild, t } = props
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.canvas.childEndpoints', { count: childEndpointIds.length })}
      </label>
      <div
        className={`min-h-[60px] rounded-lg border px-3 py-2 ${
          childEndpointIds.length > 0
            ? 'border-violet-300 bg-violet-50 dark:border-violet-700 dark:bg-violet-900/20'
            : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50'
        }`}
      >
        {childEndpointIds.length > 0 ? (
          <ChildChips childEndpointIds={childEndpointIds} onToggleChild={onToggleChild} />
        ) : (
          <span className="text-sm text-slate-400 dark:text-slate-500">
            {parentEndpointId
              ? t('infrastructure.canvas.clickEndpointsToAddAsChildren')
              : t('infrastructure.canvas.selectParentFirst')}
          </span>
        )}
      </div>
    </div>
  )
}

function ChildChips({
  childEndpointIds,
  onToggleChild,
}: {
  childEndpointIds: string[]
  onToggleChild: (id: string) => void
}): ReactElement {
  return (
    <div className="flex flex-wrap gap-1">
      {childEndpointIds.map((id) => (
        <span
          key={id}
          className="inline-flex items-center gap-1 rounded-md bg-violet-100 px-2 py-0.5 font-mono text-xs text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
        >
          {id.slice(0, ID_DISPLAY_LENGTH)}...
          <button
            onClick={() => {
              onToggleChild(id)
            }}
            className="hover:text-violet-900 dark:hover:text-violet-100"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  )
}

interface LinkTypeSelectorProps {
  selectedLinkType: LinkStatus
  setSelectedLinkType: React.Dispatch<React.SetStateAction<LinkStatus>>
  t: TFunction<'cases'>
}

export function LinkTypeSelector({
  selectedLinkType,
  setSelectedLinkType,
  t,
}: LinkTypeSelectorProps): ReactElement {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.canvas.linkType')}
      </label>
      <div className="space-y-2">
        {(Object.keys(LINK_STATUS_COLOURS) as LinkStatus[]).map((status) => (
          <LinkTypeOption
            key={status}
            status={status}
            isSelected={selectedLinkType === status}
            onSelect={() => {
              setSelectedLinkType(status)
            }}
            t={t}
          />
        ))}
      </div>
    </div>
  )
}

interface LinkTypeOptionProps {
  status: LinkStatus
  isSelected: boolean
  onSelect: () => void
  t: TFunction<'cases'>
}

function LinkTypeOption({ status, isSelected, onSelect, t }: LinkTypeOptionProps): ReactElement {
  const colour = LINK_STATUS_COLOURS[status]
  const i18nKeys = LINK_STATUS_I18N_KEYS[status]
  return (
    <button
      onClick={onSelect}
      className={`flex w-full items-start gap-3 rounded-lg border px-3 py-2 transition-all ${
        isSelected
          ? `${colour} ring-2 ring-offset-1`
          : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600'
      }`}
    >
      <div
        className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-2 ${
          isSelected ? 'border-current bg-current' : 'border-slate-300 dark:border-slate-600'
        }`}
      >
        {isSelected && <Check className="h-full w-full p-0.5 text-white" />}
      </div>
      <div className="text-left">
        <div className="text-sm font-medium">{t(i18nKeys.label as never)}</div>
        <div className="text-xs opacity-70">{t(i18nKeys.description as never)}</div>
      </div>
    </button>
  )
}

interface OpenQuestionFieldProps {
  openQuestionText: string
  setOpenQuestionText: React.Dispatch<React.SetStateAction<string>>
  t: TFunction<'cases'>
}

export function OpenQuestionField({
  openQuestionText,
  setOpenQuestionText,
  t,
}: OpenQuestionFieldProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        <HelpCircle className="mr-1 inline-block h-4 w-4" />
        {t('infrastructure.canvas.questionText')}
      </label>
      <textarea
        value={openQuestionText}
        onChange={(e) => {
          setOpenQuestionText(e.target.value)
        }}
        placeholder={t('infrastructure.canvas.questionPlaceholder')}
        className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-amber-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        rows={2}
      />
    </div>
  )
}

interface ToolFooterProps {
  canComplete: boolean
  childCount: number
  onCancel: () => void
  onComplete: () => void
  t: TFunction<'cases'>
}

export function ToolFooter({
  canComplete,
  childCount,
  onCancel,
  onComplete,
  t,
}: ToolFooterProps): ReactElement {
  return (
    <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/50">
      <button
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        {t('actions.cancel')}
      </button>
      <button
        onClick={onComplete}
        disabled={!canComplete}
        className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
          canComplete
            ? 'bg-fuchsia-600 text-white hover:bg-fuchsia-700'
            : 'cursor-not-allowed bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500'
        }`}
      >
        {childCount > 1
          ? t('infrastructure.canvas.createLinks')
          : t('infrastructure.canvas.createLink')}
      </button>
    </div>
  )
}
